import { FC, HTMLAttributes, ReactNode, useRef, useState } from 'react';
import { Modal, Image, Button } from 'react-bootstrap';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Swiper as SwiperClass } from 'swiper';
import { Pagination } from 'swiper/modules';
import useTypewriter from '../../../../Hooks/useTypewriter';
import styles from './GuidedTour.module.scss?module';
import { postRequest } from '../../../../Helper/ApiClient';
import { useAtom } from 'jotai';
import { startGuidedTourAtom } from '../../Atoms/vtsAtoms';

const GuidedTourStepsData = [
  {
    image:
      'https://files.naskorsports.com/shop/virtual-trade-show/guided-tour/Guided-Tour-image-1.jpg',
    // url: '/virtual-trade-show/exhibition/3/2',
    stepLabel: '1',
    content:
      " Welcome to the Virtual Trade Show! Take a second and let me show you our highlights. As usual benefit from great discounts and free samples of top brands. Take the chance to win a new iPhone17 by participating in our reward program. Earn your first reward points by finishing the guided tour! 😉 ",
  },
  {
    image:
      'https://files.naskorsports.com/shop/virtual-trade-show/guided-tour/Guided-Tour-image-2.jpg',
    // url: '/virtual-trade-show/exhibition/4/3',
    stepLabel: '2',
    content:
      " Cellucor,  Mars Protein, and QNT are our main sponsors this year. Don’t miss out on their exclusive deals — make sure to visit their booths, discover their products, and grab your free samples. It’s worth it! "
  },
  {
    image:
      'https://files.naskorsports.com/shop/virtual-trade-show/guided-tour/Guided-Tour-image-3.png',
    // url: '/virtual-trade-show/exhibition/5/4',
    stepLabel: '3',
    content:
      " One of our highlights this year – The Showcase Area. Here, you'll find selected top products you definitely want in your portfolio. Click on the display cases to explore everything in detail. "
  },
  {
    image:
      'https://files.naskorsports.com/shop/virtual-trade-show/guided-tour/Guided-Tour-image-4.png',
    // url: '/virtual-trade-show/exhibition/8/2',
    stepLabel: '4',
    content:
      " Find all award winners of 2025 at our Naskor booth. To make sure you get the most points at the reward program, you should click through all award winners and keep your tradeshow interaction high. Enjoy! "
  },
] as const;

const GuidedTour = () => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isSlideshowOpen, setIsSlideshowOpen] = useState(true);
  const swiperRef = useRef<SwiperCore>(null);
  const customBullets = useRef<HTMLDivElement>(null);
  const typedText = useTypewriter(
    GuidedTourStepsData[currentSlideIndex]?.content
  );
  const [isGuidedTourStarted, setIsGuidedTourStarted] =
    useAtom(startGuidedTourAtom);

  const viewGuidedTourMutation = postRequest(
    '/api/virtual-trade-show/view-guided-tour'
  );

  function submitTourCompletion() {
    viewGuidedTourMutation.mutate(JSON.stringify({}));
    setIsSlideshowOpen(false);
    setIsGuidedTourStarted(false);
  }

  return (
    <Modal
      size="xl"
      show={isSlideshowOpen || isGuidedTourStarted}
      centered
      contentClassName={styles.ModalContent}
      onHide={() => {
        if (currentSlideIndex + 1 >= GuidedTourStepsData.length) {
          submitTourCompletion();
        }
      }}
    >
      <Modal.Header
        className={`${styles.ModalHeader} py-4 py-xl-2`}
        onClick={() => {
          setIsSlideshowOpen(false);
          setIsGuidedTourStarted(false);
        }}
        closeButton
      />
      <Modal.Body className="pt-0">
        <Swiper
          className={styles.GuidedTourSwiper}
          onInit={(swiper: SwiperClass) => {
            swiperRef.current = swiper;
          }}
          pagination={{ clickable: true, el: customBullets.current }}
          modules={[Pagination]}
          onSlideChange={(swiper: SwiperClass) => {
            setCurrentSlideIndex(swiper.activeIndex);
            setTimeout(() => {
              swiper.updateAutoHeight();
            }, 0);
          }}
          autoHeight={true}
        >
          {GuidedTourStepsData.map((slide, ind) => {
            return (
              <SwiperSlide key={slide.stepLabel}>
                <Image
                  className={styles.SliderImage}
                  src={slide.image}
                  alt={`slide-image-${ind}`}
                />

                {/* Mobile an Tablet only */}
                <div className="d-xl-none pt-4">
                  <p className="m-0">
                    {GuidedTourStepsData[currentSlideIndex]?.content}
                  </p>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
        <div className="w-100 d-flex justify-content-center pt-4">
          <Button
            className={styles.NextButton}
            onClick={() => {
              if (currentSlideIndex + 1 < GuidedTourStepsData.length) {
                swiperRef.current && swiperRef.current.slideNext();
              } else {
                submitTourCompletion();
              }
            }}
          >
            {currentSlideIndex + 1 < GuidedTourStepsData.length
              ? 'Next Step'
              : 'End'}
          </Button>
        </div>
        <div
          ref={customBullets}
          className={`${styles.CustomPagination} pt-4`}
        />

        {/* Desktop Only */}
        <div className={`${styles.SpeechBubbleContainer}  d-none d-xl-block`}>
          <SpeechBubbleCard>{typedText}</SpeechBubbleCard>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default GuidedTour;

interface SpeechBubbleCardProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: ReactNode;
}

const SpeechBubbleCard: FC<SpeechBubbleCardProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div className={`${styles.GuidedTourContainer} ${className}`} {...props}>
      {children}

      <div className={styles.RobotContainer}>
        <Image
          src={
            'https://naskorsports01.ams3.cdn.digitaloceanspaces.com/assets/shop/vts/vts-2024-robot-tiny.png'
          }
          className={styles.Robot}
          alt="robot-image"
        />
      </div>
    </div>
  );
};
